import { Box, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { addDays, format, parse } from 'date-fns';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useEventStore } from '@/state/stores/use-event-store';
import { useFlowStore } from '@/state/stores/use-flow-store';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { dateToMinutesFromMidnight } from '@/helpers/date.helper';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import type { Mode } from '@/types/entity-mode';
import { useTranslations } from '@/i18n/use-translations';
import { useFlowSyncTransport } from '@/state/mutations/flows/use-flow-sync-transport';
import { buildFlowSyncEmitPayload } from '@/components/flows/detail/slot-list/build-flow-sync-emit-payload';
type FlowBasicsEventSlotTimingProps = {
  /** Flow document being edited (e.g. plan draft in comparison mode). */
  flow: FlowDto;
  mode: Mode;
  /** When true, day/time are read-only (organize mode). */
  disabled?: boolean;
};

const dayLabelId = 'flow-basics-event-slot-day-label';

export function FlowBasicsEventSlotTiming({ flow: flowProp, mode, disabled = false }: FlowBasicsEventSlotTimingProps) {
  const t = useTranslations();
  const [searchParams] = useSearchParams();
  const eventSlotIdParam = searchParams.get('eventSlotId');
  const event = useEventStore((s) => s.event);
  const setFlow = useFlowStore((s) => s.setFlow);
  const setFlowPlan = useFlowStore((s) => s.setFlowPlan);
  const setFlowOrganize = useFlowStore((s) => s.setFlowOrganize);
  const { emitFlowSync, canEmit } = useFlowSyncTransport();

  const flowId = flowProp.id;
  const flow =
    useFlowStore((s) => {
      if (s.flowPlan?.id === flowId) return s.flowPlan;
      if (s.flowOrganize?.id === flowId) return s.flowOrganize;
      if (s.flow?.id === flowId) return s.flow;
      return undefined;
    }) ?? flowProp;

  const [isEditingStartTime, setIsEditingStartTime] = useState(false);
  const [pendingStartTime, setPendingStartTime] = useState<string | null>(null);
  const didAcceptStartTimeRef = useRef(false);

  const readOnly = disabled || isOrganizeMode(mode);

  const applyOptimistic = useCallback(
    (next: FlowDto) => {
      const optimistic = {
        ...next,
        version: next.version + 1,
        durationPlannedMinutes: next.durationPlannedMinutes,
      };
      const s = useFlowStore.getState();
      if (s.flowPlan?.id === next.id) setFlowPlan(optimistic);
      if (s.flowOrganize?.id === next.id) setFlowOrganize(optimistic);
      if (s.flow?.id === next.id) setFlow(optimistic);
    },
    [setFlow, setFlowOrganize, setFlowPlan],
  );

  const emitUpdatedFlow = useCallback(
    (updated: FlowDto) => {
      if (!canEmit || !eventSlotIdParam) return;
      applyOptimistic(updated);
      void emitFlowSync(buildFlowSyncEmitPayload(updated, eventSlotIdParam));
    },
    [applyOptimistic, canEmit, emitFlowSync, eventSlotIdParam],
  );

  const startOffsetMinutes = flow.slot?.startOffsetMinutes;
  const startTimeLabel = useMemo(() => {
    if (startOffsetMinutes === undefined) return '';
    return minuteOffsetToTime(startOffsetMinutes);
  }, [startOffsetMinutes]);

  const dayDisplay = useMemo(() => {
    if (!event?.days?.length || !event.startDate) return '';
    const selectedDayId = flow.slot?.day?.id || event.days[0]?.id || '';
    const dayIndex = event.days.findIndex((d) => d.id === selectedDayId);
    if (dayIndex < 0) return '';
    return t('event.detail.addFlow.values.eventDay.value', {
      dayId: `${dayIndex + 1}`,
      date: format(addDays(event.startDate, dayIndex), 'dd/MM/yyyy'),
    });
  }, [event, flow.slot?.day?.id, t]);

  const dayFieldLabel = t('event.detail.addFlow.values.eventDay.label');
  const startTimeFieldLabel = t('event.detail.addFlow.values.startTime');

  const handleDayChange = (newDayId: string) => {
    if (!event?.days?.length || !flow.slot) return;
    const selectedDay = event.days.find((d) => d.id === newDayId);
    if (!selectedDay) return;
    const updatedFlow: FlowDto = {
      ...flow,
      slot: {
        ...flow.slot,
        day: selectedDay,
      },
    };
    emitUpdatedFlow(updatedFlow);
  };

  if (!eventSlotIdParam || !event?.days?.length || !flow.slot) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 120px',
        gap: 1,
        width: '100%',
        alignItems: 'flex-end',
      }}
    >
      {/* Day — left */}
      {readOnly ? (
        <Stack sx={{ minWidth: 0 }} spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {dayFieldLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
            {dayDisplay}
          </Typography>
        </Stack>
      ) : (
        <FormControl size="small" fullWidth variant="standard" sx={{ minWidth: 0 }}>
          <InputLabel id={dayLabelId}>{dayFieldLabel}</InputLabel>
          <Select
            labelId={dayLabelId}
            value={flow.slot?.day?.id || event.days[0]?.id || ''}
            onChange={(e) => handleDayChange(e.target.value)}
          >
            {event.days.map((day, i) => (
              <MenuItem key={`flow-basics-day-${day.id}`} value={day.id}>
                {t('event.detail.addFlow.values.eventDay.value', {
                  dayId: `${i + 1}`,
                  date: format(addDays(event.startDate!, i), 'dd/MM/yyyy'),
                })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Stack sx={{ width: 120, minWidth: 120, maxWidth: 120 }} alignItems="stretch" spacing={0.5}>
        {readOnly ? (
          <>
            <Typography variant="caption" color="text.secondary">
              {startTimeFieldLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              {startTimeLabel}
            </Typography>
          </>
        ) : (
          <>
            <TimePicker
              ampm={false}
              views={['hours', 'minutes']}
              timeSteps={{ minutes: 1 }}
              value={
                pendingStartTime
                  ? parse(pendingStartTime, 'HH:mm', new Date())
                  : startTimeLabel
                    ? parse(startTimeLabel, 'HH:mm', new Date())
                    : null
              }
              open={isEditingStartTime}
              onOpen={() => {
                didAcceptStartTimeRef.current = false;
                setIsEditingStartTime(true);
              }}
              onClose={() => {
                if (didAcceptStartTimeRef.current) {
                  didAcceptStartTimeRef.current = false;
                  setPendingStartTime(null);
                  return;
                }
                setPendingStartTime(null);
                setIsEditingStartTime(false);
              }}
              onChange={(value) => {
                if (value) {
                  setPendingStartTime(format(value, 'HH:mm'));
                }
              }}
              onAccept={(value) => {
                if (!value || !flow.slot) return;
                const newTime = format(value, 'HH:mm');
                didAcceptStartTimeRef.current = true;
                const mins = dateToMinutesFromMidnight(newTime);
                const updatedFlow: FlowDto = {
                  ...flow,
                  slot: {
                    ...flow.slot,
                    startOffsetMinutes: mins,
                  },
                };
                emitUpdatedFlow(updatedFlow);
                setPendingStartTime(null);
                setIsEditingStartTime(false);
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'standard',
                  fullWidth: true,
                  label: startTimeFieldLabel,
                  sx: {
                    width: 120,
                    maxWidth: 120,
                  },
                },
                actionBar: { actions: ['cancel', 'accept'] },
                popper: {
                  placement: 'bottom-start',
                  modifiers: [
                    { name: 'offset', options: { offset: [0, 8] } },
                    { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport', padding: 8 } },
                    { name: 'flip', enabled: true },
                  ],
                },
              }}
            />
          </>
        )}
      </Stack>
    </Box>
  );
}
